import { ErrorView } from "@/components/ErrorView";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { containerPermissions } from "@/lib/boo-permissions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useParams, useSearch } from "@tanstack/react-router";
import { Play, RefreshCw } from "lucide-react";
import { AccessTab } from "../AccessTab";
import { ExtrasTab, PeopleTab } from "../ChannelsTab";
import { PeopleV2Tab } from "./PeopleV2Tab";
import { type IdentityState, useIdentityMode } from "./use-identity";

function useIds() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  return { orgId: params.orgId ?? "", containerId: params.containerId ?? "" };
}

function Waiting() {
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 sm:p-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-96 max-w-full" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

function Updating() {
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-2 p-10 text-center">
      <RefreshCw className="size-6 animate-spin text-muted-foreground [animation-duration:2.5s]" />
      <p className="text-sm font-medium">Boo is finishing an update</p>
      <p className="max-w-sm text-sm text-muted-foreground text-pretty">
        This page will be ready in a minute or two. Everyone can keep talking to Boo meanwhile.
      </p>
    </div>
  );
}

function Stopped({ title, containerId }: { title: string; containerId: string }) {
  const queryClient = useQueryClient();
  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const boo = boos.data?.data.find((b) => b.id === containerId);
  const canStart =
    (boo?.deploymentStatus === "stopped" || boo?.deploymentStatus === "error") &&
    containerPermissions(boo, boos.data?._permissions)?.canResume === true;
  const start = useMutation({
    mutationFn: () => api.containers.start(containerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["containers"] }),
  });
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <PageHeader title={title} />
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed px-4 py-6">
        <p className="text-sm text-muted-foreground">Resume this Boo to manage who can use what.</p>
        {canStart && (
          <Button size="sm" variant="outline" loading={start.isPending} onClick={() => start.mutate()}>
            <Play />
            Resume
          </Button>
        )}
      </div>
      {start.isError && <InlineError error={start.error} />}
    </div>
  );
}

function Failed({ title, identity }: { title: string; identity: IdentityState }) {
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <PageHeader title={title} />
      <ErrorView variant="inline" title="Couldn't load this page" error={identity.error} onRetry={identity.retry} />
    </div>
  );
}

export function PeopleRoute() {
  const { containerId } = useIds();
  const identity = useIdentityMode(containerId);
  const { mode } = identity;
  if (mode === "pending") return <Waiting />;
  if (mode === "error") return <Failed title="People" identity={identity} />;
  if (mode === "stopped") return <Stopped title="People" containerId={containerId} />;
  if (mode === "legacy") return <PeopleTab />;
  if (mode === "updating") return <Updating />;
  // Keyed so nothing on the page (filters, dismissed notices) carries over to another Boo.
  return <PeopleV2Tab key={containerId} />;
}

// /can-use was the "What people can use" tab. Its settings live on People's
// Groups tab, or on Extras for older Boos.
export function CanUseRoute() {
  const { orgId, containerId } = useIds();
  const search = useSearch({ strict: false }) as { group?: string };
  const { mode } = useIdentityMode(containerId);
  if (mode === "pending") return <Waiting />;
  if (mode === "legacy") {
    return <Navigate to="/orgs/$orgId/containers/$containerId/extras" params={{ orgId, containerId }} replace />;
  }
  return (
    <Navigate
      to="/orgs/$orgId/containers/$containerId/people"
      params={{ orgId, containerId }}
      search={search.group ? { tab: "groups", group: search.group } : { tab: "groups" }}
      replace
    />
  );
}

export function ExtrasRoute() {
  const { orgId, containerId } = useIds();
  const identity = useIdentityMode(containerId);
  const { mode } = identity;
  if (mode === "pending") return <Waiting />;
  if (mode === "error") return <Failed title="Extras" identity={identity} />;
  if (mode === "stopped") return <Stopped title="Extras" containerId={containerId} />;
  if (mode === "legacy") return <ExtrasTab />;
  return <Navigate to="/orgs/$orgId/containers/$containerId/people" params={{ orgId, containerId }} search={{ tab: "groups" }} replace />;
}

export function AccessRoute() {
  const { orgId, containerId } = useIds();
  const identity = useIdentityMode(containerId);
  const { mode } = identity;
  if (mode === "pending") return <Waiting />;
  if (mode === "error") return <Failed title="Access" identity={identity} />;
  if (mode === "stopped") return <Stopped title="Access" containerId={containerId} />;
  if (mode === "legacy") return <AccessTab />;
  return <Navigate to="/orgs/$orgId/containers/$containerId/people" params={{ orgId, containerId }} replace />;
}
